import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Switch, useHistory, useLocation } from 'react-router-dom';
import Cookies from 'universal-cookie';
import BrunixSidebar from '../../components/AiBrunixPanel/BrunixSidebar';
import { Footer } from '../../components/Footer/Footer';
import { Header } from '../../components/Header/Header';
import EmployeeProfileModal from '../../components/Profile/EmployeeProfileModal';
import { Sidebar } from '../../components/Sidebar/Sidebar';
import { dashboardRoutes, PrivateRoute } from '../../configs/appRoutes';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { MOCK_USERS } from '../../mocks/Users.mocks';
import { SessionTimeoutModal } from '../../components/shared/Modal/SessionTimeoutModal';
import ChangePasswordModal from '../../components/shared/Modal/ChangePasswordModal';
import { Skeleton } from '../../components/shared/Skeleton/Skeleton';
import type { RootState } from '../../redux/store';
import SessionService from '../../services/SessionsService';
import './DashboardPage.css';

const DashboardPage: React.FC<any> = (props) => {
  const location = useLocation();
  const history = useHistory();

  const pathParts = location.pathname.split('/').filter(Boolean);
  const activeTab = pathParts.length > 0 ? pathParts[pathParts.length - 1] : 'dashboard';

  const handleTabClick = (tab: string) => {
    history.push(`/${tab === 'dashboard' ? '' : tab}`);
  };

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const [isAiBrunixOpen, setIsAiBrunixOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  const [profileModalUser, setProfileModalUser] = useState<any>(null);

  // Las vistas esperan a la sociedad activa (la carga el selector del header) y
  // se montan de nuevo al cambiarla, para que todo se recargue con la nueva.
  const { activeId: activeCompanyId, status: companyStatus } = useSelector((state: RootState) => state.activeCompany);
  const companyResolved = companyStatus === 'ready' || companyStatus === 'error';

  const { currentUser: liveCurrentUser } = useCurrentUser();
  const [cookieCurrentUser] = useState<any>(() => {
    try {
      const cookies = new Cookies();
      const session = cookies.get('101Obex');
      return session?.user || null;
    } catch {
      return null;
    }
  });
  // Prefer the live /me profile (fresh roles, real status); fall back to the
  // thin snapshot cached at login while /me is still loading or if it fails.
  const currentUser = liveCurrentUser || cookieCurrentUser;

  const [primaryColor, setPrimaryColor] = useState('#00ab84');
  const [fontFamily, setFontFamily] = useState('JetBrains Mono');
  const [animationsEnabled, setAnimationsEnabled] = useState(true);

  useEffect(() => {
    document.documentElement.style.setProperty('--accent-green', primaryColor);
    document.documentElement.style.setProperty('--font-family-mono', fontFamily);
    document.documentElement.style.setProperty('--font-family-sans', fontFamily === 'Inter' ? 'Inter' : fontFamily);
  }, [primaryColor, fontFamily]);

  useEffect(() => {
    const layoutEl = document.querySelector('.dashboard-layout');
    if (layoutEl) {
      if (animationsEnabled) {
        layoutEl.classList.add('animations-enabled');
      } else {
        layoutEl.classList.remove('animations-enabled');
      }
    }
  }, [animationsEnabled]);

  const customProps = {
    primaryColor,
    setPrimaryColor,
    fontFamily,
    setFontFamily,
    animationsEnabled,
    setAnimationsEnabled
  };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const closeAllMenus = () => {
    setIsAiBrunixOpen(false);
    setIsUserMenuOpen(false);
  };

  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('theme') as 'light' | 'dark') || 'dark';
    }
    return 'dark';
  });

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const [showSessionTimeout, setShowSessionTimeout] = useState(false);

  useEffect(() => {
    // Sin timer de sesion en PRE
    // SessionService.restoreSession();

    const handleInactivity = () => setShowSessionTimeout(true);
    document.addEventListener('inactivityEvent', handleInactivity);
    return () => {
      document.removeEventListener('inactivityEvent', handleInactivity);
    };
  }, []);

  const handleLogout = () => {
    SessionService.logout().then(() => {
      window.location.href = '/login';
    });
  };

  const handleStaySignedIn = () => {
    SessionService.restartInactivitySecondCounter();
    setShowSessionTimeout(false);
  };

  const setIsAuthenticated = (value: boolean) => {
    if (!value) {
      handleLogout();
    }
  };


  return (
    <div
      className={`dashboard-layout animations-enabled`}
      onClick={closeAllMenus}
    >
      <Sidebar
        isSidebarCollapsed={isSidebarCollapsed}
        setIsSidebarCollapsed={setIsSidebarCollapsed}
        activeTab={activeTab}
        handleTabClick={handleTabClick}
        setIsAuthenticated={setIsAuthenticated}
        currentUser={currentUser}
        users={MOCK_USERS}
        setProfileModalUser={setProfileModalUser}
      />

      <main className="dashboard-content">
        <Header
          isAiBrunixOpen={isAiBrunixOpen}
          setIsAiBrunixOpen={setIsAiBrunixOpen}
          closeAllMenus={closeAllMenus}
          toggleTheme={toggleTheme}
          theme={theme}
          currentTime={currentTime}
          isUserMenuOpen={isUserMenuOpen}
          setIsUserMenuOpen={setIsUserMenuOpen}
          setIsAuthenticated={setIsAuthenticated}
        />
        <div className={`dashboard-route-wrapper ${location.pathname.startsWith('/agents/edit/') ? 'no-padding' : ''}`}>
          {companyResolved ? (
            <Switch key={activeCompanyId || 'sin-sociedad'}>
              {dashboardRoutes.map((route, index) => (
                <PrivateRoute
                  key={index}
                  path={route.path}
                  exact={route.exact}
                  component={route.component}
                  render={route.render}
                  customProps={customProps}
                />
              ))}
            </Switch>
          ) : (
            <Skeleton width="100%" height="240px" />
          )}
        </div>
        <Footer />
      </main>

      <BrunixSidebar isOpen={isAiBrunixOpen} onClose={() => setIsAiBrunixOpen(false)} />

      <EmployeeProfileModal
        isOpen={!!profileModalUser}
        onClose={() => setProfileModalUser(null)}
        user={profileModalUser}
        onSave={(updatedUser) => {
          setProfileModalUser(null);
          // In a real app, dispatch an update to the user list
        }}
      />
      {/* Sin timer de sesion en PRE */}
      { /*
      <SessionTimeoutModal 
        isOpen={showSessionTimeout} 
        onStaySignedIn={handleStaySignedIn} 
        onSignOut={handleLogout} 
      />
      */}
      {currentUser?.must_change_password && (
        <ChangePasswordModal
          isOpen={true}
          accessToken={(() => {
            try {
              const cookies = new Cookies();
              return cookies.get('101Obex')?.token || '';
            } catch {
              return '';
            }
          })()}
          onSuccess={() => {
            try {
              const cookies = new Cookies();
              const session = cookies.get('101Obex');
              if (session && session.user) {
                session.user.must_change_password = false;
                cookies.set('101Obex', JSON.stringify(session), { path: '/' });
              }
            } catch (e) {
              console.error(e);
            }
            window.location.reload();
          }}
          onCancel={handleLogout}
          isMandatory={true}
        />
      )}
    </div>
  );
};

export default DashboardPage;
